"use client";

/**
 * Global 404 page.
 *
 * Routes the user back to the correct "home" based on their auth state:
 *   - No session          → /login
 *   - Global admin only   → /admin/companies   (platform operators home)
 *   - Anyone else signed in → /dashboard
 *
 * Next.js renders this component whenever no app route matches the URL, or
 * when a page explicitly calls `notFound()`. We keep it a Client Component
 * so we can read the in-memory AuthContext — the redirect destination
 * depends on who the user is, which is only known client-side.
 */

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth/context";
import { ThemeToggle } from "@/components/ui/theme-toggle";

interface HomeTarget {
  href: string;
  label: string;
}

function resolveHome(
  isAuthenticated: boolean,
  isGlobalAdmin: boolean,
  hasCompany: boolean,
): HomeTarget {
  if (!isAuthenticated) {
    return { href: "/login", label: "Go to sign-in" };
  }
  // Pure platform admins (global admin, no company association) belong in the
  // /admin area. Global admins who also sit inside a company keep the platform
  // dashboard as their home — matches the ProtectedRoute routing.
  if (isGlobalAdmin && !hasCompany) {
    return { href: "/admin/companies", label: "Go to companies" };
  }
  return { href: "/dashboard", label: "Go to dashboard" };
}

export default function NotFound() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const router = useRouter();

  const target = resolveHome(
    isAuthenticated,
    user?.isGlobalAdmin ?? false,
    Boolean(user?.companyId),
  );

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg px-4">
      {/* Soft accent glow — kept behind content, decorative only. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-accent/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-accent/10 blur-3xl"
      />

      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-lg text-center">
        <div className="mb-6 flex justify-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent">
            <Image
              src="/logo/logomark-white.svg"
              alt=""
              width={32}
              height={32}
              priority
            />
          </div>
        </div>

        <div className="relative mx-auto mb-2 select-none">
          <h2
            aria-hidden
            className="bg-gradient-to-b from-text-primary/15 to-text-primary/0 bg-clip-text text-[140px] leading-none font-bold tracking-tighter text-transparent sm:text-[180px]"
          >
            404
          </h2>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-text-primary sm:text-3xl">
          Page not found
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm text-text-secondary">
          The page you tried to reach doesn&apos;t exist or has moved. Pick
          up where you left off below.
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {/* Disable the link while auth state is still resolving so we never
              send a mid-restore user to /login only to bounce them again. */}
          <Link
            href={isLoading ? "#" : target.href}
            aria-disabled={isLoading}
            className={`inline-flex w-full items-center justify-center rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none sm:w-auto ${
              isLoading ? "pointer-events-none opacity-50" : ""
            }`}
          >
            {isLoading ? "Loading…" : target.label}
          </Link>
          <button
            type="button"
            onClick={() => router.back()}
            className="inline-flex w-full items-center justify-center rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none sm:w-auto"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}
